import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';

interface User {
  username: string;
  email: string;
  token: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, userData: Omit<User, 'token'>) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on app start
    const token = authService.getToken();
    if (token) {
      // Set user as authenticated with the stored token
      // In a real app, you might want to validate the token with the server
      setUser({
        username: localStorage.getItem('username') || 'User',
        email: localStorage.getItem('email') || '',
        role: localStorage.getItem('role') || '',
        token
      });
    }
    setLoading(false);
  }, []);

  const login = (token: string, userData: Omit<User, 'token'>) => {
    const userWithToken = { ...userData, token };
    setUser(userWithToken);
    authService.setToken(token);

    // Store user data in localStorage for persistence
    localStorage.setItem('username', userData.username);
    localStorage.setItem('email', userData.email);
    if (userData.role) localStorage.setItem('role', userData.role);
  };

  const logout = () => {
    setUser(null);
    authService.logout();
    // Remove user data from localStorage
    localStorage.removeItem('username');
    localStorage.removeItem('email');
    localStorage.removeItem('role');
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
